import { TimelineMax, TweenLite } from "gsap";
import { FontWeight, FontStyle } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { Translation } from "@nolimitcity/slot-game/bin/core/translation/Translation";
import { TextStyleOptions } from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import { ResizeHelper, IDimConfig, LayoutDimData } from "@nolimitcity/slot-game/bin/core/utils/ResizeHelper";
import { Point } from "pixi.js";
import { NineSliceButton } from "@nolimitcity/slot-game/bin/core/component/NineSliceButton";
import { IBackPlateMeshConfig } from "@nolimitcity/slot-game/bin/core/displayobject/BasePlateMesh";
import { StateHandler } from "@nolimitcity/slot-game/bin/core/utils/StateHandler";
import { GameResources } from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import { BaseView } from "@nolimitcity/slot-game/bin/core/base/BaseView";
import { TranslationConfig } from "../../../../translations/TranslationConfig";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {GameAssets} from "../../../../GameAssets";


export class SplashButtonType extends StateHandler {
    public static readonly CONTINUE:string = "continue";
    public static readonly RIGHT:string = "right";
    public static readonly LEFT:string = "left";
}

export class SplashButton extends BaseView {

    protected readonly continueDim: IDimConfig = {
        dimOrientation: {
            landscape: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 550),
                layout: {
                    left: new Point(360, 550),
                    right: new Point(360, 550)
                }
            },
            portrait: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 600)
            }
        },
        WIDTH: 240,
        HEIGHT: 64,
    };

    protected readonly sideDim: IDimConfig = {
        dimOrientation: {
            landscape: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 360),
                layout: {
                    left: new Point(360, 360),
                    right: new Point(360, 360)
                }
            },
            portrait: {
                scale: new Point(1.75, 1.75),
                position: new Point(360, 360)
            }
        },
        WIDTH: 240,
        HEIGHT: 64,
    };

    protected readonly DEFAULT_BUTTON_TEXT_STYLE: TextStyleOptions = {
        fontFamily: "Open Sans",
        fontStyle: FontStyle.ITALIC,
        fontSize: 34,
        fill: "#ffffff",
        fontWeight: FontWeight.EXTRA_BOLD
    };

    protected readonly DEFAULT_BTN_BACKGROUND: IBackPlateMeshConfig = {
        cornerRadius: 12,
        lineThickness: 2,
        lineColor: 0xffffff,
        lineAlpha: 1,
        backgroundColor: 0x000000,
        backgroundAlpha: 0.9
    };

    protected readonly DEFAULT_BUTTON_TEXT: string = "CONTINUE";

    protected buttonType:SplashButtonType = new SplashButtonType(SplashButtonType.CONTINUE);
    protected callBack: Function | undefined = () => { };
    protected _button: NineSliceButton | PIXI.Sprite;

    protected animationTimeline:TimelineMax;

    protected get continueBtn():NineSliceButton | undefined{
        return this.buttonType.equal(SplashButtonType.CONTINUE) ? this._button as NineSliceButton : undefined;
    }

    protected get sideBtn():PIXI.Sprite | undefined{
        return !this.buttonType.equal(SplashButtonType.CONTINUE) ? this._button as PIXI.Sprite : undefined;
    }

    constructor(cb: Function, type:string = SplashButtonType.CONTINUE) {
        super();
        this.buttonType.set(type);
        this.visible = false;
        this.callBack = cb;
    }

    protected initAnimations():void{
        this.createButtons();
    }

    protected onClick(): void {
        if (this.continueBtn && !this.continueBtn.enabled) return;
        if (this.callBack) this.callBack();
    }

    protected sideClick():void{
        Helper.timeLineKiller(this.animationTimeline);
        this.animationTimeline = new TimelineMax();
        this.animationTimeline.to(this.sideBtn!.scale,0.2,{x:1.1, y:1.1});
        this.animationTimeline.to(this.sideBtn!.scale,0.2,{x:1,y:1});
        this.animationTimeline.play();
    }

    public showButton(duration: number = 1000000000000): TimelineMax {
        const tl: TimelineMax = new TimelineMax({
            onStart: () => {
                // SlotGame.keypad.hide();
                this.visible = true;
                if(this.continueBtn) this.continueBtn.enabled = true;
                else if(this.sideBtn){
                    this.sideBtn.interactive = true;
                    this.sideBtn.buttonMode = true;
                } 
            },
            onComplete: () => { if(this.continueBtn) this.continueBtn.click(); }
        });
        tl.to(this, duration, {});
        tl.play();
        return tl;
    }

    protected createButtons(): void {
        if(this.buttonType.equal(SplashButtonType.CONTINUE))
            this._button = this.createContinue();
        else{
            this._button = this.createSide();
        }
    }

    protected createSide(): PIXI.Sprite {
        const button = new PIXI.Sprite(GameResources.getTextures(GameAssets.IN_GAME_ARROW)[0]);
        button.anchor.set(0.5,0.5);
        button.scale.set(this.buttonType.equal(SplashButtonType.RIGHT) ? 0.5 : -0.5, 0.5);
        button.interactive = false;
        button.buttonMode = false;
        button.on('pointerdown', () => this.onClick());
        this.addChild(button);
        return button;
    }

    protected createContinue(): NineSliceButton {
        const text: string = Translation.translate(TranslationConfig.CONTINUE);
        const textStyle: TextStyleOptions = this.getScaledButtonTextStyle();
        const button = new NineSliceButton(() => this.onClick(), ` ${text} `, textStyle, this.DEFAULT_BTN_BACKGROUND);
        button.setSize(this.continueDim.WIDTH, this.continueDim.HEIGHT, 1);
        button.enabled = false;
        this.addChild(button);
        return button;
    }

    protected getScaledButtonTextStyle(): TextStyleOptions {
        const style: TextStyleOptions = this.DEFAULT_BUTTON_TEXT_STYLE;
        style.fontSize = (<number>style.fontSize);
        return style;
    }

    public onResize(): void {
        const dimConf = this.buttonType.equal(SplashButtonType.CONTINUE) ? this.continueDim : this.sideDim;
        const layoutdata: LayoutDimData = ResizeHelper.calcDim(dimConf);

        this.position = layoutdata.position;
        this.scale = layoutdata.scale;

        if(!this.buttonType.equal(SplashButtonType.CONTINUE))
            this.position.x += this.buttonType.equal(SplashButtonType.RIGHT) ? 300 : -300;
    }
}
